import { macroMethods } from '@api/api/timeseries-schema';
import { formatTicker } from '@api/mappers/formatTicker';
import { mapEntity } from '@api/mappers/mapEntity';
import {
  ChartAPIReturn,
  LegendMoreMenu,
  PriceHoverData,
  SeriesType,
} from '@chart';
import { DropdownListItem } from '@design/components/dropdown/dropdown-list-item/DropdownListItem';
import { Icon } from '@design/components/icon/Icon';
import { Tooltip } from '@design/components/tooltip/Tooltip';
import { useOutsideClick } from '@helpers/hooks/use-outside-click/useOutsideClick';
import { isSnakeWithThreshold } from '@helpers/utils/entity/entity';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { ThemeChartLogo } from '@terminal/components/theme-search-logo/ThemeChartLogo';
import { TruncateTooltip } from '@terminal/components/truncate-tooltip/TruncateTooltip';
import clsx from 'clsx';
import React, { ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useChartTheme } from '../../hooks/use-chart-theme/useChartTheme';
import { UseTurboChartReturn } from '../../hooks/use-turbo-chart/useTurboChart';
import { isThemeChartEntity } from '../../utils/chart-theme-asset/chartThemeAsset';
import type {
  TerminalChartAssetData,
  TerminalChartEntity,
} from '../../utils/chart-utils/types';
import { IndicatorLegendDetails } from '../indicator-legend-details/IndicatorLegendDetails';
import { LegendPrice } from '../legend-price/LegendPrice';
import styles from './LegendItem.module.scss';

export interface LegendItemProps {
  changeChartLineColor: (colorId: number) => void;
  changeSeriesType?: (
    chartType: SeriesType,
    { snake }: { snake?: string; paneIndex?: number }
  ) => void;
  chartAssetData: TerminalChartAssetData;
  chartPaneActions: UseTurboChartReturn['chartPaneActions'];
  colorArray: string[];
  hasOHLC: boolean;
  lastPaneIndex: number;
  multiplePlotted: boolean;
  onChangeIndicator?: (entity: TerminalChartEntity) => void;
  onChangeTicker?: (entity: TerminalChartEntity, paneIndex: number) => void;
  renderLogo?: (entity: TerminalChartEntity) => ReactNode;
  onHideAsset: (entity: TerminalChartEntity, isHidden: boolean) => void;
  onRemoveItem: UseTurboChartReturn['removeActiveAsset'];
  paneIndex: number;
  selectedSeriesType?: SeriesType;
  hoverData?: PriceHoverData;
  legendLogoSize?: number;
  hasHover?: boolean;
  chartApiRef?: React.RefObject<ChartAPIReturn | null>;
}

export const LegendItem = ({
  changeChartLineColor,
  changeSeriesType,
  chartAssetData,
  chartPaneActions,
  colorArray,
  hasOHLC,
  lastPaneIndex,
  multiplePlotted,
  onChangeIndicator,
  onChangeTicker,
  onHideAsset,
  onRemoveItem,
  paneIndex,
  selectedSeriesType,
  hoverData,
  legendLogoSize = 20,
  hasHover = true,
  chartApiRef,
  renderLogo = entity => {
    if (isThemeChartEntity(entity)) {
      return (
        <ThemeChartLogo
          tag={entity.tag}
          name={entity.name}
          size={legendLogoSize <= 20 ? 16 : 32}
        />
      );
    }

    const {
      formattedTicker: _formattedTicker,
      logo: _logo,
      ...assetEntity
    } = entity;

    return <AssetLogoContainer entity={assetEntity} size={legendLogoSize} />;
  },
}: LegendItemProps) => {
  const { t } = useTranslation('chart');
  const { colors } = useChartTheme();
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const legendItemRef = useRef(null);
  const { moveBetweenPanes } = chartPaneActions;

  const legendMoreMenuLocales = {
    more: t('more'),
    tapToClose: t('tapToClose'),
    changeChartColor: t('changeChartColor'),
    seriesType_line: t('seriesType.line'),
    seriesType_bars: t('seriesType.bars'),
    seriesType_candlestick: t('seriesType.candlestick'),
  };

  const { snakeMeta, ts, lineColorToken, isHidden } = chartAssetData;
  const legendColor = colors.getThemeColor(lineColorToken);

  useOutsideClick({
    ref: legendItemRef,
    callback: () => setIsHighlighted(false),
    eventType: 'click',
  });

  const entity = chartAssetData.entity;
  const entityName = entity?.name;
  const shouldShowEntityName =
    entity?.country?.long === 'kor' || entity?.country?.long === 'jpn';

  const handleChangeColour = (colorIndex: number) => {
    changeChartLineColor(colorIndex);
  };

  const isEconomicSnake =
    snakeMeta?.method_sub_class &&
    macroMethods.includes(snakeMeta.method_sub_class);
  const mappedEntityCountry = entity.country?.long;

  const handleHideAsset = () => {
    onHideAsset(entity, !isHidden);
  };

  const onPaneMove = (direction: 'up' | 'down') => {
    moveBetweenPanes({
      paneIndex,
      snake: snakeMeta?.snake ?? entity.default_snake,
      newPaneIndex: direction === 'up' ? paneIndex - 1 : paneIndex + 1,
    });
  };

  return (
    <div
      data-element="legend-item"
      ref={legendItemRef}
      className={clsx(styles.legendItemRoot, {
        [styles.isHighlighted]: isMoreMenuOpen || isHighlighted,
        [styles.hasHover]: hasHover,
      })}
    >
      <div className={styles.legendItemDetails}>
        <div className={styles.assetContainer} data-testid="asset-container">
          <div className={styles.asset} data-element="legend-asset">
            <span
              className={styles.legendButtonWrapper}
              data-element="legend-button-wrapper"
            >
              {renderLogo(entity)}
              {shouldShowEntityName && entityName && (
                <TruncateTooltip
                  data-testid="legend-item-truncate-tooltip"
                  text={entityName}
                  atLength={13}
                  placement="top"
                  inPortal
                  fallbackLabel={
                    <div
                      className={styles.entityNameWrapper}
                      data-element="legend-entity-name"
                    >
                      {entityName}
                    </div>
                  }
                  truncatedLabelWrapper={
                    <div
                      className={styles.entityNameWrapper}
                      data-element="legend-entity-name"
                    />
                  }
                />
              )}
              <Tooltip
                label={t('chart:changeData')}
                closeLabel={t('chart:tapToClose')}
                isTouchDevice={false}
                inPortal
                disabled={!onChangeTicker}
              >
                <button
                  data-testid="legend-ticker"
                  data-element="legend-button"
                  className={clsx(styles.button, {
                    [styles.isUpperCase]: !isThemeChartEntity(entity),
                  })}
                  onClick={
                    onChangeTicker
                      ? () => onChangeTicker(entity, paneIndex)
                      : undefined
                  }
                >
                  {isEconomicSnake && mappedEntityCountry
                    ? mappedEntityCountry
                    : isThemeChartEntity(entity)
                      ? entity.name
                      : formatTicker(entity)}
                </button>
              </Tooltip>
            </span>
            {onChangeIndicator && !isThemeChartEntity(entity) && (
              <>
                <Icon className={styles.separatorDot} iconName="Dot" size={6} />
                <span
                  className={styles.legendButtonWrapper}
                  data-element="legend-button-wrapper"
                >
                  <Tooltip
                    label={t('chart:changeIndicator')}
                    closeLabel={t('chart:tapToClose')}
                    isTouchDevice={false}
                    inPortal
                  >
                    <button
                      data-testid="legend-indicator"
                      data-element="legend-button"
                      className={styles.button}
                      onClick={() => {
                        onChangeIndicator(entity);
                      }}
                    >
                      {snakeMeta?.name.english}
                    </button>
                  </Tooltip>
                </span>
              </>
            )}
          </div>
        </div>
        <div className={styles.menuButtonContainer}>
          <LegendMoreMenu
            chartColor={legendColor}
            colorArray={colorArray}
            onColorSelect={handleChangeColour}
            onMenuOpen={open => setIsMoreMenuOpen(open)}
            changeSeriesType={changeSeriesType}
            hasOHLC={hasOHLC}
            selectedSeriesType={selectedSeriesType}
            defaultSnake={snakeMeta?.snake}
            locales={legendMoreMenuLocales}
            renderMovePane={() => (
              <>
                {(paneIndex !== 0 || multiplePlotted) && (
                  <DropdownListItem
                    label={t('chart:moveUp')}
                    leadingIcon={<Icon iconName="ArrowUp" size={16} />}
                    isActive={false}
                    isFocused={false}
                    onClick={() => onPaneMove('up')}
                  />
                )}
                {(paneIndex !== lastPaneIndex || multiplePlotted) && (
                  <DropdownListItem
                    label={t('chart:moveDown')}
                    leadingIcon={<Icon iconName="ArrowDown" size={16} />}
                    isActive={false}
                    isFocused={false}
                    onClick={() => onPaneMove('down')}
                  />
                )}
              </>
            )}
          />
          <Tooltip
            label={isHidden ? t('chart:show') : t('chart:hide')}
            closeLabel={t('chart:tapToClose')}
            isTouchDevice={false}
            inPortal
          >
            <span className={styles.menuIconSpan}>
              <Icon
                iconName={isHidden ? 'HidePassword' : 'ShowPassword'}
                size={12}
                onClick={handleHideAsset}
              />
            </span>
          </Tooltip>
          <Tooltip
            label={t('chart:remove')}
            closeLabel={t('chart:tapToClose')}
            isTouchDevice={false}
            inPortal
          >
            <span className={styles.menuIconSpan}>
              <Icon
                iconName="Close"
                data-testid="Close-icon"
                size={12}
                onClick={() => onRemoveItem(entity.default_snake)}
              />
            </span>
          </Tooltip>
        </div>
      </div>
      {snakeMeta && (
        <div className={styles.priceContainer}>
          {isSnakeWithThreshold(snakeMeta) ? (
            <IndicatorLegendDetails
              chartAssetData={chartAssetData}
              hoverData={hoverData}
            />
          ) : (
            <LegendPrice
              entity={mapEntity(entity)}
              snakeMeta={snakeMeta}
              isChartHovered={false}
              isPrimary={hasOHLC}
              legendColor={legendColor}
              selectedSeriesType={selectedSeriesType}
              ts={ts}
              hoverData={hoverData}
              chartApiRef={chartApiRef}
            />
          )}
        </div>
      )}
    </div>
  );
};
